import { TMasterStandardIndustryClassification } from "@/api/entities/master-standard-industry-classification.entity";
import { useUpdateStandardIndustryClassification } from "@/api/services/global/master";
import { IconPlusCircle } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { DataConflictModal } from "@/components/commons/DataConflictModal";
import { useDialog } from "@/hooks/useDialog";
import { useStandardIndustryConflictModal } from "@/features/master-data/hooks/useStandardIndustryConflictModal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { EMPTY_STR } from "@/constants/string";
import { ClassificationCodeField } from "@/features/master-data/components/StandardIndustry/ClassificationCodeField";
import { ClassificationFormModal } from "@/features/master-data/components/StandardIndustry/ClassificationFormModal";
import { HiddenClassificationModal } from "@/features/master-data/components/StandardIndustry/HiddenClassificationModal";
import { StandardIndustryStyledTable } from "@/features/master-data/components/StandardIndustry/standardIndustryTable.styles";
import { STANDARD_INDUSTRY_CHILD_LEVEL, STANDARD_INDUSTRY_LEVEL_LABEL } from "@/features/master-data/constants/standardIndustry";
import { makeStandardIndustryClassificationSchema } from "@/features/master-data/schema/masterDataStandardIndustrySchema";
import { TStandardIndustryClassificationForm } from "@/features/master-data/types/masterDataStandardIndustryForm";
import { useVisible } from "@/hooks/useVisible";
import { background, text } from "@/theme/colors";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography, styled } from "@mui/material";
import { FC, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

type TClassificationNodeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  node: TMasterStandardIndustryClassification;
  groupId: number;
  getChildren: (parentId: number) => TMasterStandardIndustryClassification[];
};

export const ClassificationNodeModal: FC<TClassificationNodeModalProps> = ({ isOpen, onClose, node, groupId, getChildren }) => {
  const updateMutation = useUpdateStandardIndustryClassification();
  const createChildModal = useVisible();
  const [editingChild, setEditingChild] = useState<TMasterStandardIndustryClassification>();
  const [hiddenChild, setHiddenChild] = useState<TMasterStandardIndustryClassification>();

  const childLevel = STANDARD_INDUSTRY_CHILD_LEVEL[node.level];
  const children = childLevel ? getChildren(toNumber(node.id)) : [];

  const schema = useMemo(() => makeStandardIndustryClassificationSchema(node.level), [node.level]);
  const parentFullCode = node.fullCode.slice(0, node.fullCode.length - node.code.length);

  const methods = useForm<TStandardIndustryClassificationForm>({
    resolver: yupResolver(schema),
    defaultValues: { code: node.code, name: node.name },
    mode: "all",
    reValidateMode: "onBlur",
  });

  const {
    formState: { isDirty },
  } = methods;

  const confirmClose = useDialog();
  const conflict = useStandardIndustryConflictModal(onClose);

  const handleRequestClose = async () => {
    if (!isDirty) {
      onClose();
      return;
    }
    const isConfirmed = await confirmClose.show();
    if (isConfirmed) {
      onClose();
    }
  };

  const onSubmit = async (data: TStandardIndustryClassificationForm) => {
    try {
      const rs = await updateMutation.mutateAsync({
        id: toNumber(node.id),
        payload: { code: data.code, name: data.name, updatedAt: node.updatedAt },
      });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      onClose();
    } catch (error) {
      if (conflict.handleConflictError(error)) return;
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal
      width={900}
      title={`${STANDARD_INDUSTRY_LEVEL_LABEL[node.level]} ${node.fullCode} の編集`}
      isOpen={isOpen}
      enableFooter={false}
      onClose={handleRequestClose}
      enableCloseIcon={false}
    >
      <Form methods={methods} onSubmit={onSubmit} schema={schema}>
        <Stack gap={3} py={3}>
          <ClassificationCodeField control={methods.control} parentFullCode={parentFullCode} />
          <FormField control={methods.control} name="name">
            <Input />
          </FormField>

          {childLevel && (
            <Stack gap={1}>
              <Typography variant="body14Bold">{STANDARD_INDUSTRY_LEVEL_LABEL[childLevel]}</Typography>
              <StandardIndustryStyledTable sx={{ maxHeight: "40vh" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>分類コード</TableCell>
                      <TableCell>分類名</TableCell>
                      <TableCell sx={{ textAlign: "center !important" }}>有効／無効</TableCell>
                      <TableCell sx={{ textAlign: "center !important" }}>編集</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {children.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: "center !important", padding: "12px !important" }}>
                          <Typography variant="body14" color={text.tertiary}>
                            {EMPTY_STR.TEXT}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      children.map((child) => (
                        <TableRow key={child.id}>
                          <TableCell sx={{ minWidth: "120px", paddingLeft: "8px !important" }}>
                            <Typography color={child.isHidden ? text.tertiary : text.primary} variant="body14">
                              {child.fullCode || EMPTY_STR.TEXT}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: "260px", paddingLeft: "8px !important" }}>
                            <Typography color={child.isHidden ? text.tertiary : text.primary} variant="body14">
                              {child.name || EMPTY_STR.TEXT}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: "center !important", minWidth: "110px" }}>
                            <Button variant="outline" size="sm" onClick={() => setHiddenChild(child)}>
                              {child.isHidden ? "有効化" : "無効化"}
                            </Button>
                          </TableCell>
                          <TableCell sx={{ textAlign: "center !important", minWidth: "90px", paddingX: "4px !important" }}>
                            <Button size="sm" onClick={() => setEditingChild(child)}>
                              編集
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </StandardIndustryStyledTable>
              <AddRowButton onClick={() => createChildModal.open()}>
                <IconPlusCircle />
                <Typography variant="cap12">{`${STANDARD_INDUSTRY_LEVEL_LABEL[childLevel]}を追加する`}</Typography>
              </AddRowButton>
            </Stack>
          )}
        </Stack>

        <Stack flexDirection="row" justifyContent="center" gap="24px" sx={{ position: "sticky", bottom: 0, bgcolor: background.white, pt: 2 }}>
          <Button variant="outline" onClick={handleRequestClose}>
            閉じる
          </Button>
          <Button type="submit" isLoading={updateMutation.isPending}>
            保存
          </Button>
        </Stack>
      </Form>

      {childLevel && createChildModal.visible && (
        <ClassificationFormModal
          isOpen={createChildModal.visible}
          onClose={createChildModal.close}
          groupId={groupId}
          parentId={toNumber(node.id)}
          level={childLevel}
          parentFullCode={node.fullCode}
          parentUpdatedAt={node.updatedAt}
        />
      )}

      {editingChild && (
        <ClassificationNodeModal
          isOpen={Boolean(editingChild)}
          onClose={() => setEditingChild(undefined)}
          node={editingChild}
          groupId={groupId}
          getChildren={getChildren}
        />
      )}

      <HiddenClassificationModal hiddenClassification={hiddenChild} setHiddenClassification={setHiddenChild} />

      <ConfirmSaveModal open={confirmClose.open} onOk={confirmClose.confirm} onClose={confirmClose.cancel}>
        <Typography>入力したデータがなくなりますが、よろしいですか。</Typography>
      </ConfirmSaveModal>
      <DataConflictModal
        open={conflict.isConflictOpen}
        name={conflict.conflictName}
        onConfirm={conflict.confirmReload}
        onCancel={conflict.cancelConflict}
      />
    </Modal>
  );
};

const AddRowButton = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  width: fit-content;
`;
